import { useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useUserDataFlattened } from "@/modules/user/stores";
import {
  usePopupTimingByType,
  useRechargeActivityList,
  useIsHomeDisplayReady,
  useDisplayItems,
  useActivities,
} from "@/modules/homeDisplay/hooks";
import { TimingType, ActivityType } from "@/modules/homeDisplay/types";
import type { ActivityPopupItem } from "@/modules/homeDisplay/types";
import { ROUTES } from "@/types/routes";
import { POPUP_IDS } from "@/config/popupConfig";
import { popupTriggerManager, triggerPopup } from "@/utils/popupTriggers";
import type { BalanceInsufficientPopupResult } from "../types/balanceInsufficientPopup.types";
import { isGroupedRechargeTaskFloatingFixedId } from "@/components/Event/groupedRechargeTaskFloatingId";
import { isGroupedBonusPopupPending } from "@/components/Event/groupedRechargeBonusFields";

const DEFAULT_BALANCE_THRESHOLD = 10;

const ACTIVITY_TYPE_TO_POPUP_ID: Partial<Record<ActivityType, string>> = {
  [ActivityType.RED_PACKET_RAIN]: POPUP_IDS.RED_PACKET,
  [ActivityType.FIRST_DEPOSIT]: POPUP_IDS.FIRST_RECHARGE,
  [ActivityType.FIVE_DAILY_RECHARGE]: POPUP_IDS.CASHBACK,
  [ActivityType.RECHARGE_PROMOTION]: POPUP_IDS.RECHARGE_OFFER,
  [ActivityType.GROUPED_RECHARGE_TASK]: POPUP_IDS.RECHARGE_OFFER,
  [ActivityType.DAILY_RECHARGE]: POPUP_IDS.SEVEN_DAY_RECHARGE,
  [ActivityType.SIGN_REWARD]: POPUP_IDS.CHECK_IN,
  [ActivityType.BET_TASK]: POPUP_IDS.BET_ACTIVITY,
  // RECHARGE_ACTIVITY 和 RECHARGE_TASK_OLD 需要特殊处理
};

export const useBalanceInsufficientPopup =
  (): BalanceInsufficientPopupResult => {
    const { data: userInfo } = useUserDataFlattened();
    const location = useLocation();
    const timing = usePopupTimingByType(TimingType.BALANCE_INSUFFICIENT);
    const rechargeActivityList = useRechargeActivityList();
    const isConfigReady = useIsHomeDisplayReady();
    const displayItems = useDisplayItems();
    const activities = useActivities();

    const userId = userInfo?.userId || "guest";
    const isTriggeringRef = useRef(false);
    const hasTriggeredRef = useRef(false);

    // 提取有效的充值任务（用于 RECHARGE_TASK_OLD）
    const rechargeTasks = useMemo(() => {
      if (!activities?.RECHARGE_TASK_OLD?.list || !displayItems) {
        return [];
      }

      const tasks = activities.RECHARGE_TASK_OLD.list;
      const floatingItems = displayItems.filter(
        (item: any) =>
          item.position === "FLOATING_ICON" &&
          item.sourceType === "ACTIVITY" &&
          item.activityType === "RECHARGE_TASK_OLD"
      );

      // 匹配 taskId 和 activityId
      const matched: Array<{ task: any; displayItem: any }> = [];

      for (const displayItem of floatingItems) {
        const task = tasks.find(
          (t: any) => t.taskId === (displayItem as any).activityId
        );
        if (task) {
          matched.push({ task, displayItem });
        }
      }

      // 按 sort 降序排序
      return matched.sort(
        (a, b) =>
          ((b.displayItem as any).sort || 0) -
          ((a.displayItem as any).sort || 0)
      );
    }, [activities, displayItems]);

    // GROUPED_RECHARGE_TASK：合并为 grouped-all 弹窗；固定位时不再按 taskGroupId 逐条配
    const groupedRechargeTasks = useMemo(() => {
      if (!activities?.GROUPED_RECHARGE_TASK?.list || !displayItems) {
        return [];
      }

      const tasks = activities.GROUPED_RECHARGE_TASK.list;
      const floatingItems = displayItems.filter(
        (item: any) =>
          item.position === "FLOATING_ICON" &&
          item.sourceType === "ACTIVITY" &&
          item.activityType === "GROUPED_RECHARGE_TASK"
      );

      const fixedOnes = floatingItems.filter((item: any) =>
        isGroupedRechargeTaskFloatingFixedId((item as any).activityId)
      );

      if (fixedOnes.length > 0 && tasks.length > 0) {
        const displayItem = fixedOnes.reduce((top: any, item: any) =>
          (item.sort || 0) > (top.sort || 0) ? item : top
        );
        const totalReward = (tasks as { rewardAmount?: number }[]).reduce(
          (s, t) => s + Number(t.rewardAmount ?? 0),
          0
        );
        const grt = activities.GROUPED_RECHARGE_TASK as Record<
          string,
          unknown
        >;
        return [
          {
            task: {
              taskId: "grouped-all",
              tasks: tasks.map((t: any) => ({
                ...t,
                taskId: t.taskGroupId,
              })),
              rewardAmount: totalReward,
              bonusPopupPending: grt.bonusPopupPending,
              popupItems: grt.popupItems,
              popupRewardAmount: grt.popupRewardAmount,
            },
            displayItem,
          },
        ];
      }

      const matched: Array<{ task: any; displayItem: any }> = [];

      for (const displayItem of floatingItems) {
        const task = tasks.find(
          (t: any) => t.taskGroupId === (displayItem as any).activityId
        );
        if (task) {
          matched.push({
            task: { ...task, taskId: task.taskGroupId },
            displayItem,
          });
        }
      }

      return matched.sort(
        (a, b) =>
          ((b.displayItem as any).sort || 0) -
          ((a.displayItem as any).sort || 0)
      );
    }, [activities, displayItems]);

    const rechargeTasksForPopup = useMemo(
      () => [...rechargeTasks, ...groupedRechargeTasks],
      [rechargeTasks, groupedRechargeTasks]
    );

    // 获取活动的余额阈值
    const getBalanceThreshold = useCallback(
      (activity: ActivityPopupItem): number => {
        // 对于 RECHARGE_ACTIVITY，优先从 activityEntryPoint 获取 popupTimingBalance
        if (
          activity.activityType === ActivityType.RECHARGE_ACTIVITY &&
          activity.activityId
        ) {
          const rechargeActivity = rechargeActivityList.find(
            item => item?.activityGroupId === activity.activityId
          );
          if (
            rechargeActivity?.activityEntryPoint?.popupTimingBalance != null
          ) {
            return rechargeActivity.activityEntryPoint.popupTimingBalance;
          }
        }

        // 其他情况使用 ActivityPopupItem 的 popupTimingBalance
        return activity.popupTimingBalance ?? DEFAULT_BALANCE_THRESHOLD;
      },
      [rechargeActivityList]
    );

    // 检查单个活动是否满足条件
    const checkActivityCondition = useCallback(
      (activity: ActivityPopupItem): boolean => {
        const { activityType, activityId } = activity;

        // 1. 检查余额阈值
        const balance = userInfo?.balance ?? 0;
        const threshold = getBalanceThreshold(activity);
        if (balance >= threshold) {
          return false;
        }

        // 2. 检查活动类型条件
        switch (activityType) {
          case ActivityType.FIVE_DAILY_RECHARGE: {
            const currentPromoRatio =
              userInfo?.customRechargeRules?.find(
                rule => rule.count === userInfo?.rechargeCount
              )?.ruleList?.[0]?.promoRatio ?? 0;
            return currentPromoRatio > 0 && !!userInfo?.firstRechargeFlag;
          }
          case ActivityType.RECHARGE_PROMOTION: {
            if (activityId) {
              const task = rechargeTasksForPopup.find(
                t => t.task.taskId === activityId
              );
              return !!task && task.task.rewardAmount > 0;
            }
            return false;
          }
          case ActivityType.GROUPED_RECHARGE_TASK: {
            if (
              !activityId ||
              isGroupedRechargeTaskFloatingFixedId(activityId)
            ) {
              const grouped = rechargeTasksForPopup.find(
                t => t.task.taskId === "grouped-all"
              );
              return (
                !!grouped &&
                (Number(grouped.task.rewardAmount ?? 0) > 0 ||
                  isGroupedBonusPopupPending(grouped.task))
              );
            }
            const task = rechargeTasksForPopup.find(
              t => t.task.taskId === activityId
            );
            return !!task && task.task.rewardAmount > 0;
          }
          case ActivityType.RECHARGE_ACTIVITY:
          case ActivityType.BET_TASK:
            return true;
          default:
            return false;
        }
      },
      [userInfo, rechargeTasksForPopup, getBalanceThreshold]
    );

    // 获取下一个满足条件的活动
    const getNextValidActivity = useCallback(() => {
      if (!timing || !timing.enabled) return null;

      const candidateActivities = timing.activityItems;
      if (candidateActivities.length === 0) return null;

      // 排序：先按优先级，再按 sort
      const sortedActivities = [...candidateActivities].sort((a, b) => {
        const priorityDiff =
          (a.popupPriority || 999) - (b.popupPriority || 999);
        if (priorityDiff !== 0) return priorityDiff;
        return a.sort - b.sort;
      });

      // 从 localStorage 读取本次会话中已弹出过的活动
      const today = new Date().toISOString().split("T")[0];
      const sessionKey = `balance_insufficient_shown_${userId}_${today}`;
      const shownKeys = JSON.parse(
        localStorage.getItem(sessionKey) || "[]"
      ) as string[];

      // 找到第一个未弹出且满足条件的活动
      const nextActivity = sortedActivities.find(activity => {
        const popupKey = activity.activityId
          ? `${activity.activityId}_${activity.activityType}`
          : (activity.activityType ?? "");

        // 跳过已弹出的
        if (shownKeys.includes(popupKey)) {
          return false;
        }

        // 检查是否满足条件
        return checkActivityCondition(activity);
      });

      return nextActivity || null;
    }, [timing, userId, userInfo, checkActivityCondition]);

    const shouldShow = useMemo(() => {
      if (!isConfigReady) return false;
      const activity = getNextValidActivity();
      return !!activity;
    }, [isConfigReady, getNextValidActivity]);

    const trigger = (): void => {
      if (!isConfigReady) return;

      // 防止重复触发
      if (isTriggeringRef.current) {
        return;
      }

      isTriggeringRef.current = true;

      // 获取下一个满足条件的活动
      const currentActivity = getNextValidActivity();
      if (!currentActivity) {
        isTriggeringRef.current = false;
        return;
      }

      const { activityId, activityType, groupId } = currentActivity;
      const popupKey = activityId
        ? `${activityId}_${activityType}`
        : (activityType ?? "");

      // 记录到 localStorage（标记为已弹出）
      const today = new Date().toISOString().split("T")[0];
      const sessionKey = `balance_insufficient_shown_${userId}_${today}`;
      const shownKeys = JSON.parse(
        localStorage.getItem(sessionKey) || "[]"
      ) as string[];
      if (!shownKeys.includes(popupKey)) {
        shownKeys.push(popupKey);
        localStorage.setItem(sessionKey, JSON.stringify(shownKeys));
      }

      // 触发弹窗
      if (activityType === ActivityType.RECHARGE_ACTIVITY && groupId) {
        // RECHARGE_ACTIVITY 通过 groupId 触发
        setTimeout(() => {
          triggerPopup(`recharge-promotion-auto-${groupId}`);
          isTriggeringRef.current = false;
        }, 100);
      } else if (activityType === ActivityType.GROUPED_RECHARGE_TASK) {
        // 与 useRechargeOfferPopup 合并弹窗 id 一致
        setTimeout(() => {
          triggerPopup("recharge-task-grouped-all");
          isTriggeringRef.current = false;
        }, 100);
      } else if (
        activityType === ActivityType.RECHARGE_PROMOTION &&
        activityId
      ) {
        setTimeout(() => {
          triggerPopup(`recharge-task-${activityId}`);
          isTriggeringRef.current = false;
        }, 100);
      } else if (activityType) {
        // 其他活动类型通过 popupId 触发
        const popupId = ACTIVITY_TYPE_TO_POPUP_ID[activityType];
        if (popupId && popupTriggerManager.isRegistered(popupId)) {
          setTimeout(() => {
            popupTriggerManager.trigger(popupId);
            isTriggeringRef.current = false;
          }, 100);
        } else {
          isTriggeringRef.current = false;
        }
      } else {
        isTriggeringRef.current = false;
      }
    };

    useEffect(() => {
      if (typeof window === "undefined") return;

      const isOnHome =
        location.pathname === ROUTES.HOME ||
        location.pathname === ROUTES.HOME_ALT;
      if (!isOnHome) {
        hasTriggeredRef.current = false;
        return;
      }

      const cameFromGameDetail = sessionStorage.getItem("cameFromGameDetail");
      if (!cameFromGameDetail) return;

      // 防止同一次返回多次触发
      if (hasTriggeredRef.current) {
        return;
      }

      sessionStorage.removeItem("cameFromGameDetail");

      // shouldShow 已包含余额阈值检查
      if (!shouldShow) {
        return;
      }

      hasTriggeredRef.current = true;

      // 延迟触发确保页面已完全加载
      setTimeout(() => {
        trigger();
      }, 300);
    }, [location.pathname, shouldShow]);

    return {
      trigger,
      shouldShow,
    };
  };
